import React, { useCallback, useEffect } from "react";
import { useAppSelector } from "@src/store/hooks";
import { SelectYup } from "@src/components/select/SelectYup";
import { SubmitHandler, useForm } from "react-hook-form";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { useSearchParams } from "react-router-dom";

import { Input } from "@src/components/input/Input";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { FilterField } from "@src/components/filtersPanel/FilterField";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { FilterSection } from "@src/components/filtersPanel/FilterSection";
import { IVendor } from "@src/store/passwordManager/getVendors";

type FormValues = {
	companyId: string | number;
	search: string;
	username: string;
	vendor: string;
	onboarded: string;
	subscribed: string;
	isValid: string;
};

const onboardingOptions = [
	{ label: "Onboarded", value: "true" },
	{ label: "Not Onboarded", value: "false" },
];

const subscriptionOptions = [
	{ label: "Subscribed", value: "true" },
	{ label: "Not Subscribed", value: "false" },
];

const credentialOptions = [
	{ label: "Valid", value: "valid" },
	{ label: "Invalid", value: "invalid" },
	{ label: "Not Checked", value: "not_checked" },
];

const legacyQueryNames: Array<string> = ["email", "onboardingStatus", "subscriptionStatus", "credentialStatus"];

const allQueryNames: Array<string> = [
	"companyId",
	"search",
	"username",
	"vendorId",
	"onboarded",
	"subscribed",
	"isValid",
	...legacyQueryNames,
	"page",
];

const emptyFormValues: FormValues = {
	companyId: "",
	search: "",
	username: "",
	vendor: "",
	onboarded: "",
	subscribed: "",
	isValid: "",
};

const normalizeIsValidParam = (value: string | null) => {
	if (value === "true") return "valid";
	if (value === "false") return "invalid";
	return value ?? "";
};

export const PasswordManagerHeader: React.FC = () => {
	const [params, setParams] = useSearchParams();
	const setFormValues = useCallback((paramsValue: URLSearchParams): FormValues => {
		const companyParam = paramsValue.get("companyId");
		const parsedCompanyId =
			companyParam !== null && !Number.isNaN(Number(companyParam)) ? Number(companyParam) : (companyParam ?? "");

		return {
			companyId: parsedCompanyId,
			search: paramsValue.get("search") ?? "",
			username: paramsValue.get("username") ?? paramsValue.get("email") ?? "",
			vendor: paramsValue.get("vendorId") ?? "",
			onboarded: paramsValue.get("onboarded") ?? paramsValue.get("onboardingStatus") ?? "",
			subscribed: paramsValue.get("subscribed") ?? paramsValue.get("subscriptionStatus") ?? "",
			isValid: normalizeIsValidParam(paramsValue.get("isValid")),
		};
	}, []);

	const { control, reset, handleSubmit } = useForm<FormValues>({
		mode: "onChange",
		defaultValues: setFormValues(params),
	});

	const isAdmin = useAppSelector((state) => state.settings.isAdmin);
	const { data: companies, loading } = useAppSelector((state) => state.passwordManager.companies);
	const { data: vendors } = useAppSelector((state) => state.passwordManager.vendors);
	const companyAutoPOEnabled = useAppSelector((state) => state.passwordManager.companyAutoPO.data);
	const companyOptions = companies.map((x) => ({ label: x.name, value: x.companyId }));
	const vendorOptions: Array<{ label: string; value: string }> = (vendors as Array<IVendor>).map((v) => ({
		label: v.name,
		value: String(v.vendorId),
	}));
	const hideOnboardingFilters = !isAdmin && companyAutoPOEnabled === false;

	useEffect(() => {
		reset(setFormValues(params));
	}, [params, reset, setFormValues]);

	const onSubmit: SubmitHandler<FormValues> = (data) => {
		const nextParams = new URLSearchParams(params);
		const setOrDelete = (key: string, val: string | number | undefined) => {
			if (val === undefined || val === null || val === "") nextParams.delete(key);
			else nextParams.set(key, String(val));
		};

		setOrDelete("companyId", data.companyId);
		setOrDelete("search", data.search);
		setOrDelete("username", data.username);
		setOrDelete("vendorId", data.vendor);

		if (!hideOnboardingFilters) {
			setOrDelete("onboarded", data.onboarded);
			setOrDelete("subscribed", data.subscribed);
		} else {
			nextParams.delete("onboarded");
			nextParams.delete("subscribed");
		}

		setOrDelete("isValid", data.isValid);

		legacyQueryNames.forEach((key) => nextParams.delete(key));
		nextParams.delete("page");
		setParams(nextParams);
	};

	const handleResetFilters = () => {
		const nextParams = new URLSearchParams(params);
		allQueryNames.forEach((key) => nextParams.delete(key));
		setParams(nextParams);
		reset(emptyFormValues);
	};

	return (
		<form className={styles.panelForm} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.panelBody}>
				<FilterSection title="Search">
					<FilterField label="Search">
						<Input control={control} name="search" placeholder="Search" />
					</FilterField>

					{isAdmin ? (
						<FilterField label="Company">
							<SelectYup
								onFocus={() => mixpanelTrack(MixpanelCategory.COMPANY_NAME_FILTER)}
								isLoading={loading}
								name="companyId"
								placeholder="Search for company"
								control={control}
								options={companyOptions}
							/>
						</FilterField>
					) : null}
				</FilterSection>

				<FilterSection title="Account">
					<FilterField label="Username">
						<Input control={control} name="username" placeholder="user@example.com" />
					</FilterField>

					<FilterField label="Vendor">
						<SelectYup control={control} name="vendor" placeholder="Choose a vendor" options={vendorOptions} />
					</FilterField>
				</FilterSection>

				<FilterSection title="Status">
					{!hideOnboardingFilters ? (
						<>
							<FilterField label="Onboarding status">
								<SelectYup
									control={control}
									name="onboarded"
									placeholder="Select onboarding status"
									options={onboardingOptions}
								/>
							</FilterField>

							<FilterField label="Subscription status">
								<SelectYup
									control={control}
									name="subscribed"
									placeholder="Select subscription status"
									options={subscriptionOptions}
								/>
							</FilterField>
						</>
					) : null}

					<FilterField label="Credential status">
						<SelectYup
							control={control}
							name="isValid"
							placeholder="Select credential status"
							options={credentialOptions}
						/>
					</FilterField>
				</FilterSection>
			</div>

			<div className={styles.panelFooter}>
				<Button type="button" variant={ButtonVariant.Secondary} onClick={handleResetFilters}>
					Reset filters
				</Button>
				<Button type="submit">Search</Button>
			</div>
		</form>
	);
};
